import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import { employeeIncidentService } from '@/api/domains/employeeIncident';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { IncidentStatus } from '@/api/domains/employeeIncident';


export interface UseMyIncidentsExportParams {
  startDate: string;
  endDate: string;
  errorTypeId: number | null;
  status: IncidentStatus;
}

export interface UseMyIncidentsExportResult {
  exportIncidents: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export function useMyIncidentsExport(
  params: UseMyIncidentsExportParams,
): UseMyIncidentsExportResult {
  const { t: translate } = useTranslation('attendance');
  const { showNotification } = useUI();
  const employeeId = useSelector((state: RootState) => state.auth.user?.idEmpleado);

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => employeeIncidentService.exportIncidents(
      employeeId!,
      params.startDate,
      params.endDate,
      params.errorTypeId,
      params.status,
    ),
    successMessage: translate('incidents.messages.exportSuccess'),
    errorMessage: translate('incidents.messages.exportError'),
  });

  const exportIncidents = useCallback(async (format: UIExportFormat) => {
    if (format !== 'EXCEL') {
      showNotification('error', translate('incidents.messages.exportError'), 5000);
      return;
    }
    if (!employeeId) {
      showNotification('error', translate('incidents.messages.exportError'), 5000);
      return;
    }
    await exportFile();
  }, [exportFile, employeeId, showNotification, translate]);

  return { exportIncidents, isExporting };
}
